// @givmo/fundraisers-module — a campaign's pledge template on its Campaign tab (FCM-WEB34; D-FCM-61's decision
// 3 as drawn, frames 07, 08 and 08b): the one screen where an organizer sets a pledge its campaign adds to
// the pages and games under it (GFU-BE64's `GET`/`PUT /fundraisers/{id}/pledge-template`, the OWNER rank's).
// D-FCM-53 (RULED 2026-10-22 08:32 PDT): it says up front which pages the pledge reaches. "Who's being
// counted?" decides it, so each answer states its reach: a pledge about each joiner's own count goes on
// every page made by joining (the server's `beneath` copy); one that names who's counted stays on the
// campaign's own pages and games (its `organizer` copy). The preview is the server's own sentence.
//
// An edit changes only what the form shows: the template as stored keeps its words, live counting, who sets
// the rate and when pledging closes (review-88's F1), and one the form can't make (a yes/no, a flat amount,
// a season total, a condition) is shown as stored, with no Edit.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  idempotencyKey,
  isGivmoError,
  sdk,
  type P2pConfigOut,
  type PledgeTemplateOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DatePicker,
  Field,
  RadioTile,
  RadioTileGroup,
  toast,
} from '@givmo/design-system';
import { useFundraisersModule } from './fundraisers-context';
import { series } from './layers-copy';
import { deadlineZone, layerRefusal } from './layers-decode';
import { useLivePage } from './use-live-page';
import { SEASON_COPY as S } from './season-setup-copy';
import {
  MoneyField,
  PreviewBlock,
  ReachLine,
  TemplateTermsList,
  useSettled,
} from './season-setup-page';
import { pledgeTemplateOptions, rulePreviewOptions } from './season-setup-queries';
import {
  EMPTY_TEMPLATE_FORM,
  cardCanEdit,
  cardPreviewBody,
  cardRatePolicy,
  cardTemplateBody,
  formOfTemplate,
  storedPreviewBody,
  templateFormReady,
  templateUnits,
  type TemplateForm,
  type TemplateWho,
} from './pledge-template-decode';

export function PledgeTemplateCard({
  campaignId,
  campaignName,
  editable,
  examplePerson,
  config,
  ownCharities,
}: {
  campaignId: string;
  campaignName: string;
  /** The OWNER rank, on a campaign that hasn't ended. */
  editable: boolean;
  /** A person whose page is under the campaign: the preview's example of a joiner's page. */
  examplePerson: string | null;
  /** The campaign's settings: who picks its charities, and its list. */
  config: P2pConfigOut;
  /** The names of this campaign's own charities. */
  ownCharities: readonly string[];
}) {
  const client = useQueryClient();
  const live = useLivePage();
  // Its days start and end on the organization's own clock, as the deadline beside it does
  // (`deadlineZone`: the organization's zone, else the reader's).
  const zone = deadlineZone(useFundraisersModule().orgTimeZone);
  const stored = useQuery({ ...pledgeTemplateOptions(campaignId), retry: false });
  // The form while it is open; null while the card shows what's stored.
  const [form, setForm] = useState<TemplateForm | null>(null);
  const set = <K extends keyof TemplateForm>(key: K, value: TemplateForm[K]) =>
    setForm((now) => (now ? { ...now, [key]: value } : now));
  const base = stored.data ?? null;
  const policy = cardRatePolicy(base);
  const named = form?.who === 'named';
  const units = templateUnits(form ?? { unit: base?.measure.unit_label ?? '' }, S.unitsFallback);
  const person = form ? (named ? form.subject.trim() : (examplePerson ?? '')) : '';
  const ready = form !== null && templateFormReady(form, policy) && person !== '';
  const previewAsk = useSettled(
    ready ? cardPreviewBody(campaignId, form, person, zone, new Date(), base) : null,
    400,
  );
  const preview = useQuery(rulePreviewOptions(previewAsk));
  const save = useMutation({
    mutationFn: (write: TemplateForm) =>
      sdk.pledgesSetPledgeTemplate({
        path: { fundraiser_id: campaignId },
        body: cardTemplateBody(write, zone, base),
        ...idempotencyKey(crypto.randomUUID()),
        throwOnError: true,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: pledgeTemplateOptions(campaignId).queryKey });
      if (!live()) return;
      setForm(null);
      toast.success(S.templateSaved);
    },
    onError: (error) => {
      if (live()) toast.error(layerRefusal(error).message);
    },
  });
  // The read answers the campaign's OWNER rank only, the one rank its host mounts the card for.
  if (!stored.isSuccess) return null;
  const canEdit = editable && (base === null || cardCanEdit(base));
  return (
    <Card flush testId="campaign-pledge-template" className="text-pretty">
      <CardHeader
        title={S.templateTitle}
        subtitle={S.templateSubtitle}
        actions={
          canEdit && form === null ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => setForm(base ? formOfTemplate(base) : EMPTY_TEMPLATE_FORM)}
              testId={base ? 'pledge-template-edit' : 'pledge-template-set'}
            >
              {base ? S.edit : S.setTemplate}
            </Button>
          ) : undefined
        }
      />
      <CardBody className="flex flex-col gap-16">
        {form === null ? (
          base ? (
            <StoredTemplate
              template={base}
              campaignId={campaignId}
              campaignName={campaignName}
              examplePerson={examplePerson}
              config={config}
              ownCharities={ownCharities}
            />
          ) : (
            <p className="text-s13 text-text-secondary" data-testid="pledge-template-none">
              {S.templateNone}
            </p>
          )
        ) : (
          <fieldset
            disabled={save.isPending}
            className="m-0 flex min-w-0 flex-col gap-16 border-0 p-0"
          >
            <div className="flex flex-col gap-8">
              <p className="text-s13 font-w500 text-text-secondary">{S.whosCounted}</p>
              <RadioTileGroup
                value={form.who}
                onValueChange={(value) => set('who', value as TemplateWho)}
                aria-label={S.whosCounted}
                testId="pledge-template-who"
              >
                <RadioTile
                  value="joiner"
                  label={S.eachJoiner}
                  description={S.reachJoiner(campaignName, units)}
                  descriptionTone="secondary"
                  testId="pledge-template-joiner"
                />
                <RadioTile
                  value="named"
                  label={S.someoneElse}
                  description={S.reachNamed(campaignName)}
                  descriptionTone="secondary"
                  testId="pledge-template-named"
                />
              </RadioTileGroup>
            </div>
            <div className="grid gap-16 sm:grid-cols-2">
              {named && (
                <Field
                  label={S.namedLabel}
                  value={form.subject}
                  maxLength={120}
                  onChange={(event) => set('subject', event.target.value)}
                  containerClassName="sm:col-span-2"
                  testId="pledge-template-subject"
                />
              )}
              <Field
                label={S.whatCounted}
                value={form.unit}
                placeholder={S.unitPlaceholder}
                helper={S.writeItAsOne}
                maxLength={80}
                onChange={(event) => set('unit', event.target.value)}
                testId="pledge-template-unit"
              />
              <Field
                label={form.unit.trim() ? S.mostExpected(units) : S.mostExpectedFallback}
                value={form.most}
                inputMode="numeric"
                helper={S.mostChecked}
                onChange={(event) => set('most', event.target.value.replace(/\D/g, ''))}
                testId="pledge-template-most"
              />
              <DatePicker
                label={S.starts}
                value={form.starts}
                onValueChange={(value) => set('starts', value)}
                testId="pledge-template-starts"
              />
              <DatePicker
                label={S.ends}
                value={form.ends}
                onValueChange={(value) => set('ends', value)}
                testId="pledge-template-ends"
              />
              {/* Labelled by who sets the rate as stored, as the setup's rate fields are; a rate the
                  pledgors choose has no field (`RateFields`). */}
              {policy !== 'pledgor_choice' && (
                <MoneyField
                  label={policy === 'fixed' ? S.rate : S.suggestedRate}
                  value={form.rate}
                  trailing={S.perUnit(form.unit.trim() || 'point')}
                  onChange={(value) => set('rate', value)}
                  testId="pledge-template-rate"
                />
              )}
              <MoneyField
                label={S.capLabel}
                value={form.cap}
                helper={S.capNote}
                onChange={(value) => set('cap', value)}
                testId="pledge-template-cap"
              />
            </div>
            {previewAsk !== null && (
              <div
                className="flex flex-col gap-8 border-t border-border-subtle pt-16"
                data-testid="pledge-template-preview"
              >
                <p className="text-s13 font-w500 text-text-secondary">{S.pledgorsWillSee}</p>
                <PreviewBlock
                  preview={preview.data ?? null}
                  previewing
                  refusal={
                    preview.isError && isGivmoError(preview.error) && preview.error.status === 400
                      ? layerRefusal(preview.error).message
                      : null
                  }
                  failed={preview.isError}
                  heading={named ? S.onEachPage : S.onPersonsPage(person)}
                  testIdPrefix="pledge-template"
                />
              </div>
            )}
            <div className="flex flex-col-reverse gap-8 sm:flex-row sm:justify-end">
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => setForm(null)}
                testId="pledge-template-cancel"
              >
                {S.cancel}
              </Button>
              <Button
                appearance="saas"
                disabled={!templateFormReady(form, policy) || save.isPending}
                loading={save.isPending}
                onClick={() => save.mutate(form)}
                testId="pledge-template-save"
              >
                {S.saveTemplate}
              </Button>
            </div>
          </fieldset>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Who receives a pledge's money, where the campaign's settings say it: its own charities, where it picks
 * them, which every copy of the pledge pays. Where its pages and layers pick from a list, each copy pays its
 * own pick (a game's nightly fundraiser the one its setup made, a joiner's page its own), never the whole
 * list, so the card names none (review-88's second UI round, read against the live act).
 */
function recipientsLine(config: P2pConfigOut, ownCharities: readonly string[]): string | null {
  if ((config.recipient_choice ?? 'campaign') === 'supporters') return null;
  return series(ownCharities) || null;
}

/** A template stored on the campaign: where it goes (D-FCM-53), whose count, its terms, and its sentence. */
function StoredTemplate({
  template,
  campaignId,
  campaignName,
  examplePerson,
  config,
  ownCharities,
}: {
  template: PledgeTemplateOut;
  campaignId: string;
  campaignName: string;
  examplePerson: string | null;
  config: P2pConfigOut;
  ownCharities: readonly string[];
}) {
  const module = useFundraisersModule();
  const { measure } = template;
  const named = (measure.subject_name ?? '').trim() !== '';
  const units = templateUnits({ unit: measure.unit_label }, S.unitsFallback);
  // A stored template reads on the clock it was set on.
  const sentenceAsk = storedPreviewBody(
    campaignId,
    template,
    examplePerson,
    measure.timezone ?? deadlineZone(module.orgTimeZone),
  );
  const sentence = useQuery(rulePreviewOptions(sentenceAsk));
  // A charity's own campaign raises for itself (Q8), which every line of its card already says; a company's
  // campaign says whose the money is (tester-5's walk), where every copy pays the same charities.
  const receives =
    module.flavor.recipientMode === 'self' ? null : recipientsLine(config, ownCharities);
  return (
    <div className="flex flex-col gap-12" data-testid="pledge-template-stored">
      <ReachLine testId="pledge-template-reach">
        {named ? S.reachNamed(campaignName) : S.reachJoiner(campaignName, units)}
      </ReachLine>
      <TemplateTermsList template={template} testId="pledge-template-terms" />
      {receives && (
        <div className="flex flex-col gap-4 text-s13" data-testid="pledge-template-receives">
          <p className="text-text-secondary">{S.whoReceives}</p>
          <p className="font-w600 text-text-strong">{receives}</p>
        </div>
      )}
      {sentenceAsk !== null && sentence.isSuccess && (
        <div
          className="flex flex-col gap-8 border-t border-border-subtle pt-12"
          data-testid="pledge-template-sentence"
        >
          <p className="text-s13 font-w500 text-text-secondary">{S.pledgorsWillSee}</p>
          <PreviewBlock
            preview={sentence.data}
            previewing
            refusal={null}
            failed={false}
            heading={
              named ? S.onEachPage : S.onPersonsPage(sentenceAsk.measure?.subject_name ?? '')
            }
            testIdPrefix="pledge-template-stored"
          />
        </div>
      )}
      <p
        className="text-s12 leading-h15 text-text-secondary"
        data-testid="pledge-template-later-only"
      >
        {S.laterOnly}
      </p>
    </div>
  );
}
